import {
  LEAD_RESULT_FILTERS,
  LEAD_SOURCE_GROUP_KEYS,
  type LeadResultFilter,
  type LeadSourceGroup,
} from '@repo/shared/marketing';
import { DATE_PRESETS, type DatePreset } from './date-range-select';

/**
 * The Leads filters as URL search params, so the campaign hub and the report can deep-link a pre-filtered
 * list (`/marketing/leads?campaignId=…&result=needs_follow_up`) and Back undoes a filter. The free-text
 * search stays on the page (it changes on every keystroke). Absent = "All".
 */
export interface LeadsSearch {
  campaignId?: string;
  /** Where the lead really came from (source group). */
  source?: LeadSourceGroup;
  /** The person's outcome, as the KPI cards count it. */
  result?: LeadResultFilter;
  /** Sign-up date range on the salon calendar; absent = all time. */
  range?: Exclude<DatePreset, 'all'>;
}

const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;

const RANGES = DATE_PRESETS.filter((preset): preset is Exclude<DatePreset, 'all'> => preset !== 'all');

/** Route `validateSearch`: unknown or malformed values fall back to "All" instead of failing the page. */
export function parseLeadsSearch(search: Record<string, unknown>): LeadsSearch {
  const campaignId = typeof search.campaignId === 'string' && /^[\w-]{1,64}$/.test(search.campaignId) ? search.campaignId : undefined;
  return {
    campaignId,
    source: oneOf(search.source, LEAD_SOURCE_GROUP_KEYS),
    result: oneOf(search.result, LEAD_RESULT_FILTERS),
    range: oneOf(search.range, RANGES),
  };
}

/** True when any URL filter is set. */
export function hasLeadsSearchFilters(search: LeadsSearch): boolean {
  return Boolean(search.campaignId || search.source || search.result || search.range);
}
